import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Button, Modal, Typography } from "@trimble/modus-cmx";
import { useTranslation } from "react-i18next";
import { usePermissionChecker } from "./PermissionContext";
import type { PermissionAction } from "./types";

type GuardedAction = () => void;

type SupportAdminWriteGuardContextValue = {
  guardWriteAction: (
    action: PermissionAction,
    onAllowed: GuardedAction
  ) => boolean;
  isSupportAdminDialogOpen: boolean;
  closeSupportAdminDialog: () => void;
};

type SupportAdminViewOnlyDialogProps = {
  open: boolean;
  onClose: () => void;
};

type SupportAdminWriteGuardProviderProps = {
  children: ReactNode;
};

const SupportAdminWriteGuardContext = createContext<
  SupportAdminWriteGuardContextValue | undefined
>(undefined);

const SupportAdminViewOnlyDialog = (props: SupportAdminViewOnlyDialogProps) => {
  const { open, onClose } = props;
  const { t } = useTranslation();

  return (
    <Modal
      open={open}
      onClose={onClose}
      modalId="support-admin-view-only-modal"
      width={520}
      data-testid="support-admin-view-only-modal"
    >
      <div slot="header">
        {t("access_denied.support_admin_view_only_title")}
      </div>
      <Typography slot="content" hierarchy="h5" weight="normal">
        {t("access_denied.support_admin_view_only_description")}
      </Typography>
      <Button
        slot="footer"
        onButtonClick={onClose}
        size="sm"
        shape="rectangle"
        color="primary"
        variant="filled"
      >
        {t("access_denied.support_admin_view_only_ok")}
      </Button>
    </Modal>
  );
};

export const SupportAdminWriteGuardProvider = (
  props: SupportAdminWriteGuardProviderProps
) => {
  const { children } = props;
  const { isSupportAdminWriteBlocked } = usePermissionChecker();
  const [dialogOpen, setDialogOpen] = useState(false);

  const closeSupportAdminDialog = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const guardWriteAction = useCallback(
    (action: PermissionAction, onAllowed: GuardedAction) => {
      if (isSupportAdminWriteBlocked(action)) {
        setDialogOpen(true);
        return false;
      }

      onAllowed();
      return true;
    },
    [isSupportAdminWriteBlocked]
  );

  const contextValue = useMemo(
    () => ({
      guardWriteAction,
      isSupportAdminDialogOpen: dialogOpen,
      closeSupportAdminDialog,
    }),
    [closeSupportAdminDialog, dialogOpen, guardWriteAction]
  );

  return (
    <SupportAdminWriteGuardContext.Provider value={contextValue}>
      {children}
      {dialogOpen ? (
        <SupportAdminViewOnlyDialog
          open={dialogOpen}
          onClose={closeSupportAdminDialog}
        />
      ) : null}
    </SupportAdminWriteGuardContext.Provider>
  );
};

export const useSupportAdminWriteGuard =
  (): SupportAdminWriteGuardContextValue => {
    const context = useContext(SupportAdminWriteGuardContext);
    if (!context) {
      throw new Error(
        "useSupportAdminWriteGuard must be used within a PermissionProvider"
      );
    }
    return context;
  };
